import _cloneDeep from 'lodash/cloneDeep';
import { prepareTemplateForSave } from 'services/templateSaveUtils';
import { getHoursMinutesFormat } from 'services/utils';
import { Duration } from '../../../../modules/alerts/rules/rules-detail/duration/duration.controller';
import moment from 'moment';
import {
    getProjectsInScope,
    projectHasSensors,
    projectHasTimezone
} from '../template-projects-utils';

const locationModalTemplate = require('../template-detail/location-modal/location-modal.html');
const LocationModalController = require('../template-detail/location-modal/location-modal.controller').default;

const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/* @ngInject */
export default class BulkEditTemplatesModalController {
    constructor($mdDialog, $scope, DialogService, mode, selectedTemplates, allProjects, RulesApiService) {
        this.$mdDialog = $mdDialog;
        this.$scope = $scope;
        this.DialogService = DialogService;
        this.mode = mode;
        this.selectedTemplates = selectedTemplates;
        this.allProjects = allProjects || [];
        this.RulesApiService = RulesApiService;

        this.isApplying = false;
        this.results = null;

        // Mode-specific state
        this.initModeState();
    }

    initModeState() {
        switch (this.mode) {
            case 'activateDeactivate':
                this.enabled = true;
                break;

            case 'acknowledgement':
                this.acknowledgmentDuration = { hours: 4, minutes: 0 };
                this.invalidAcknowledgmentDuration = false;
                break;

            case 'resolvedNotifications':
                this.resolvedNotifications = true;
                this.batteryTemplateCount = this.selectedTemplates.filter(
                    t => t.trigger?.field === 'batteryStatus'
                ).length;
                break;

            case 'glycolFilter':
                this.glycolFilterEnabled = true;
                this.nonTemperatureCount = this.selectedTemplates.filter(
                    t => t.trigger?.field !== 'temperature'
                ).length;
                break;

            case 'schedule':
                this.initScheduleState();
                break;

            default:
                break;
        }
    }

    initScheduleState() {
        this.dayOptions = dayOptions;
        this.scheduleAction = 'apply'; // 'apply' or 'remove'

        // Build time options (same as template-detail)
        this.timeOptions = [];
        for (let hour = 0; hour < 24; hour++) {
            this.timeOptions.push({
                value: `${hour},${0}`,
                displayValue: moment({ hour }).format(getHoursMinutesFormat())
            });
            this.timeOptions.push({
                value: `${hour},${30}`,
                displayValue: moment({ hour, minute: 30 }).format(getHoursMinutesFormat())
            });
        }
        this.timeOptions.push({
            value: `${23},${59}`,
            displayValue: moment({ hour: 23, minute: 59 }).format(getHoursMinutesFormat())
        });

        this.defaultScheduleStartTime = '8,0';
        this.defaultScheduleEndTime = '17,0';
        this.scheduleStartIndex = this.timeOptions.map(e => e.value).indexOf(this.defaultScheduleStartTime);
        this.scheduleEndIndex = this.timeOptions.map(e => e.value).indexOf(this.defaultScheduleEndTime);

        this.scheduleMode = 'EVERYDAY';
        this.scheduleInverse = false;
        this.scheduleTimezoneOption = 'use-project-timezone';

        this.activeScheduleDays = dayOptions.slice(0);
        this.customScheduleIndexes = [];

        // Initialize time values (also set via ng-init in template, but explicit is better)
        this.scheduleStartTime = this.defaultScheduleStartTime;
        this.scheduleEndTime = this.defaultScheduleEndTime;
        this.customStartTimes = {};
        this.customEndTimes = {};

        // User timezone
        this.userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

        // Project-timezone warning state
        this.projectsWithoutTimezone = [];
        this.checkProjectsWithoutTimezone();
    }

    hasScheduleWithProjectTimezone() {
        return this.scheduleAction === 'apply' && this.scheduleTimezoneOption === 'use-project-timezone';
    }

    getRelevantProjectsAcrossTemplates() {
        const seen = new Set();
        const result = [];
        this.selectedTemplates.forEach(template => {
            getProjectsInScope(template, this.allProjects).forEach(project => {
                if (!seen.has(project.name)) {
                    seen.add(project.name);
                    result.push(project);
                }
            });
        });
        return result;
    }

    scheduleTimezoneOptionChanged() {
        this.checkProjectsWithoutTimezone();
    }

    checkProjectsWithoutTimezone() {
        if (!this.hasScheduleWithProjectTimezone()) {
            this.projectsWithoutTimezone = [];
            return;
        }
        this.projectsWithoutTimezone = this.getRelevantProjectsAcrossTemplates().filter(project => {
            return projectHasSensors(project) && !projectHasTimezone(project);
        });
    }

    openLocationModal() {
        this.DialogService.show({
            template: locationModalTemplate,
            controller: LocationModalController,
            controllerAs: '$ctrl',
            parent: document.body,
            clickOutsideToClose: false,
            escapeToClose: true,
            fullscreen: false,
            locals: {
                projects: this.projectsWithoutTimezone,
                allProjects: this.allProjects
            }
        }).then(updatedProjects => {
            this.applyLocationUpdates(updatedProjects);
        }).catch(updatedProjects => {
            // Dialog cancelled — still apply any locations updated before closing
            this.applyLocationUpdates(updatedProjects);
        });
    }

    applyLocationUpdates(updatedProjects) {
        if (Array.isArray(updatedProjects) && updatedProjects.length > 0) {
            updatedProjects.forEach(updated => {
                const idx = this.allProjects.findIndex(p => p.name === updated.name);
                if (idx !== -1) {
                    this.allProjects[idx] = { ...this.allProjects[idx], location: updated.location };
                }
            });
        }
        this.checkProjectsWithoutTimezone();
    }

    getModeTitle() {
        switch (this.mode) {
            case 'activateDeactivate': return 'Enable or disable alert template';
            case 'acknowledgement': return 'Edit acknowledgment duration';
            case 'resolvedNotifications': return 'Toggle resolved notifications';
            case 'glycolFilter': return 'Toggle virtual glycol filter';
            case 'schedule': return 'Edit schedule';
            default: return 'Bulk Edit';
        }
    }

    acknowledgmentDurationChanged() {
        const duration = new Duration(0, this.acknowledgmentDuration.minutes, this.acknowledgmentDuration.hours);
        this.invalidAcknowledgmentDuration = duration.momentDuration.asSeconds() < 60 || duration.momentDuration.asSeconds() > 604800;
    }

    get isInputValid() {
        switch (this.mode) {
            case 'acknowledgement':
                return !this.invalidAcknowledgmentDuration;
            case 'schedule':
                return this.scheduleAction === 'remove' || this.activeScheduleDays.length > 0;
            default:
                return true;
        }
    }

    // Preview helpers
    formatDuration(durationString) {
        if (!durationString) return '4h 0m (default)';
        try {
            const d = Duration.fromString(durationString);
            return `${d.hours}h ${d.minutes}m`;
        } catch (e) {
            return durationString;
        }
    }

    formatSchedule(schedule) {
        if (!schedule || !schedule.slots || schedule.slots.length === 0) return 'No schedule';
        const days = schedule.slots.flatMap(s => s.days || []);
        const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

        let dayLabel;
        if (days.length === 7) {
            dayLabel = 'Every day';
        } else if (days.length === 5 && weekdays.every(d => days.includes(d))) {
            dayLabel = 'Weekdays';
        } else {
            dayLabel = `${days.length} days`;
        }

        const modeLabel = schedule.inverse ? 'Block' : 'Send';

        // Timezone label
        let tzLabel = '';
        if (schedule.useProjectTimeLocation) {
            tzLabel = 'Project TZ';
        } else if (schedule.timezone) {
            tzLabel = schedule.timezone;
        }

        // Include time range if available
        const firstSlot = schedule.slots[0];
        if (firstSlot && firstSlot.times && firstSlot.times.length > 0) {
            const time = firstSlot.times[0];
            const formatTime = (t) => {
                if (typeof t === 'string') {
                    // Handle comma-separated format from select values (e.g., "8,0")
                    const parts = t.split(',');
                    if (parts.length === 2) {
                        return `${parts[0]}:${String(parts[1]).padStart(2, '0')}`;
                    }
                    return t;
                }
                if (t && t.hour !== undefined) return `${t.hour}:${String(t.minute || 0).padStart(2, '0')}`;
                return '';
            };
            const startStr = formatTime(time.start);
            const endStr = formatTime(time.end);
            if (startStr && endStr) {
                const suffix = tzLabel ? ` (${tzLabel})` : '';
                return `${dayLabel}, ${modeLabel} ${startStr}-${endStr}${suffix}`;
            }
        }

        const suffix = tzLabel ? ` (${tzLabel})` : '';
        return `${dayLabel}, ${modeLabel}${suffix}`;
    }

    getCurrentValue(template) {
        switch (this.mode) {
            case 'activateDeactivate':
                return template.enabled ? 'Enabled' : 'Disabled';
            case 'acknowledgement':
                return this.formatDuration(template.unacknowledgesAfter);
            case 'resolvedNotifications':
                return template.resolvedNotifications ? 'On' : 'Off';
            case 'glycolFilter':
                if (template.trigger?.field !== 'temperature') return 'N/A (not temperature)';
                return template.trigger?.range?.filter?.productEquivalentTemperature ? 'On' : 'Off';
            case 'schedule':
                return this.formatSchedule(template.schedule);
            default:
                return '';
        }
    }

    getNewValue(template) {
        switch (this.mode) {
            case 'activateDeactivate':
                return this.enabled ? 'Enabled' : 'Disabled';
            case 'acknowledgement':
                return `${this.acknowledgmentDuration.hours || 0}h ${this.acknowledgmentDuration.minutes || 0}m`;
            case 'resolvedNotifications':
                // Battery templates keep their value, so they're left out of the change
                if (this.resolvedNotifications && template.trigger?.field === 'batteryStatus') {
                    return this.getCurrentValue(template);
                }
                return this.resolvedNotifications ? 'On' : 'Off';
            case 'glycolFilter':
                if (template.trigger?.field !== 'temperature') return 'Skipped';
                return this.glycolFilterEnabled ? 'On' : 'Off';
            case 'schedule':
                if (this.scheduleAction === 'remove') return 'No schedule';
                return this.formatSchedule(this.buildSchedulePreview());
            default:
                return '';
        }
    }

    willChange(template) {
        return this.getCurrentValue(template) !== this.getNewValue(template);
    }

    get changedCount() {
        return this.selectedTemplates.filter(t => this.willChange(t)).length;
    }

    buildSchedulePreview() {
        const schedule = { inverse: this.scheduleInverse, useProjectTimeLocation: false, timezone: '', slots: [] };

        if (this.scheduleTimezoneOption === 'use-project-timezone') {
            schedule.useProjectTimeLocation = true;
        } else {
            schedule.timezone = this.scheduleTimezoneOption;
        }

        const defaultTimes = [{
            start: this.scheduleStartTime || this.defaultScheduleStartTime,
            end: this.scheduleEndTime || this.defaultScheduleEndTime
        }];

        switch (this.scheduleMode) {
            case 'EVERYDAY':
                schedule.slots = [{ days: dayOptions.slice(0), times: defaultTimes }];
                break;
            case 'WEEKDAYS':
                schedule.slots = [{ days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], times: defaultTimes }];
                break;
            case 'CUSTOM':
                this.activeScheduleDays.forEach(day => {
                    schedule.slots.push({
                        days: [day],
                        times: [{
                            start: this.customStartTimes?.[day] || this.defaultScheduleStartTime,
                            end: this.customEndTimes?.[day] || this.defaultScheduleEndTime
                        }]
                    });
                });
                break;

            default:
                break;
        }
        return schedule;
    }

    // Schedule helper methods
    scheduleModeChanged() {
        this.activeScheduleDays = [];
        this.customScheduleIndexes = [];

        this.scheduleStartIndex = this.timeOptions.map(e => e.value).indexOf(this.defaultScheduleStartTime);
        this.scheduleEndIndex = this.timeOptions.map(e => e.value).indexOf(this.defaultScheduleEndTime);

        switch (this.scheduleMode) {
            case 'EVERYDAY':
                this.activeScheduleDays = this.dayOptions.slice(0);
                break;
            case 'WEEKDAYS':
                this.activeScheduleDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
                break;
            case 'CUSTOM':
                this.activeScheduleDays = this.dayOptions.slice(0);
                this.dayOptions.forEach(() => {
                    this.customScheduleIndexes.push({ start: this.scheduleStartIndex, end: this.scheduleEndIndex });
                });
                break;

            default:
                break;
        }
    }

    setStartIndex(day, time) {
        if (this.scheduleMode !== 'CUSTOM') {
            this.scheduleStartIndex = this.timeOptions.map(e => e.value).indexOf(time);
        } else {
            const dayIndex = this.dayOptions.indexOf(day);
            this.customScheduleIndexes[dayIndex].start = this.timeOptions.map(e => e.value).indexOf(time);
        }
    }

    setEndIndex(day, time) {
        if (this.scheduleMode !== 'CUSTOM') {
            this.scheduleEndIndex = this.timeOptions.map(e => e.value).indexOf(time);
        } else {
            const dayIndex = this.dayOptions.indexOf(day);
            this.customScheduleIndexes[dayIndex].end = this.timeOptions.map(e => e.value).indexOf(time);
        }
    }

    scheduleDayToggle(day) {
        const index = this.activeScheduleDays.indexOf(day);
        if (index !== -1) {
            this.activeScheduleDays.splice(index, 1);
        } else {
            this.activeScheduleDays.push(day);
        }
    }

    buildScheduleObject() {
        const schedule = {
            inverse: this.scheduleInverse,
            timezone: '',
            useProjectTimeLocation: false,
            slots: []
        };

        // Set timezone
        if (this.scheduleTimezoneOption === 'use-project-timezone') {
            schedule.useProjectTimeLocation = true;
            schedule.timezone = '';
        } else if (this.scheduleTimezoneOption === 'UTC') {
            schedule.timezone = 'UTC';
        } else {
            schedule.timezone = this.scheduleTimezoneOption;
        }

        // Build slots based on mode
        switch (this.scheduleMode) {
            case 'EVERYDAY':
            case 'WEEKDAYS':
                schedule.slots = [{
                    days: this.activeScheduleDays.slice(0),
                    times: [{
                        start: this.scheduleStartTime || this.defaultScheduleStartTime,
                        end: this.scheduleEndTime || this.defaultScheduleEndTime
                    }]
                }];
                break;

            case 'CUSTOM':
                this.activeScheduleDays.forEach((day) => {
                    schedule.slots.push({
                        days: [day],
                        times: [{
                            start: this.customStartTimes?.[day] || this.defaultScheduleStartTime,
                            end: this.customEndTimes?.[day] || this.defaultScheduleEndTime
                        }]
                    });
                });
                break;

            default:
                break;
        }

        return schedule;
    }

    applyFieldChange(template) {
        switch (this.mode) {
            case 'activateDeactivate':
                template.enabled = this.enabled;
                break;

            case 'acknowledgement': {
                const duration = new Duration(0, this.acknowledgmentDuration.minutes, this.acknowledgmentDuration.hours);
                template.unacknowledgesAfter = duration.toString();
                break;
            }

            case 'resolvedNotifications':
                if (this.resolvedNotifications && template.trigger?.field === 'batteryStatus') {
                    return false; // Battery templates can't send resolved notifications
                }
                template.resolvedNotifications = this.resolvedNotifications;
                break;

            case 'glycolFilter':
                if (template.trigger?.field !== 'temperature') {
                    return false; // Skip non-temperature templates
                }
                if (this.glycolFilterEnabled) {
                    if (!template.trigger.range) {
                        template.trigger.range = {};
                    }
                    template.trigger.range.filter = { productEquivalentTemperature: {} };
                } else if (template.trigger.range) {
                    template.trigger.range.filter = null;
                }
                break;

            case 'schedule':
                if (this.scheduleAction === 'remove') {
                    delete template.schedule;
                } else {
                    template.schedule = this.buildScheduleObject();
                }
                break;

            default:
                break;
        }
        return true;
    }

    async applyBulkEdit() {
        if (!this.isInputValid || this.isApplying) return;

        this.isApplying = true;
        this.$scope.$applyAsync();

        let succeeded = 0;
        let failed = 0;
        let skipped = 0;
        const errors = [];

        // Build list of templates that need updating
        const updates = [];
        this.selectedTemplates.forEach(template => {
            if (!this.willChange(template)) {
                skipped++;
                return;
            }

            const modified = _cloneDeep(template);
            const shouldUpdate = this.applyFieldChange(modified);

            if (!shouldUpdate) {
                skipped++;
                return;
            }

            updates.push({ template, modified });
        });

        // Process updates in batches to avoid overwhelming the API
        const BATCH_SIZE = 5;
        for (let i = 0; i < updates.length; i += BATCH_SIZE) {
            const batch = updates.slice(i, i + BATCH_SIZE);
            const results = await Promise.allSettled( // eslint-disable-line no-await-in-loop
                batch.map(async ({ template, modified }) => {
                    const prepared = prepareTemplateForSave(modified);
                    await this.RulesApiService.updateOrganizationRule(prepared);
                    return template;
                })
            );

            for (let j = 0; j < results.length; j++) {
                if (results[j].status === 'fulfilled') {
                    succeeded++;
                } else {
                    failed++;
                    const error = results[j].reason;
                    let errorMsg;
                    if (error.data?.error) {
                        errorMsg = error.data.error;
                    } else if (error.data?.message) {
                        errorMsg = error.data.message;
                    } else if (error.status) {
                        errorMsg = `Server error (${error.status})`;
                    } else {
                        errorMsg = 'An unexpected error occurred';
                    }
                    errors.push({ name: batch[j].template.displayName, message: errorMsg });
                }
            }
        }

        this.results = { succeeded, failed, skipped, errors };

        if (failed === 0) {
            this.$mdDialog.hide(this.results);
        } else {
            // Keep modal open to show errors
            this.$scope.$applyAsync();
        }
    }

    closeDialog() {
        if (this.results) {
            this.$mdDialog.hide(this.results);
        } else {
            this.$mdDialog.cancel();
        }
    }
}
